/**
 * "Referenced by N events" — the page → event direction (`080-calendar.md` §8).
 *
 * This is the second half of the wiki-graph payoff: the NPC page shows both *which pages
 * mention him* (backlinks) and *which sessions he is scheduled to appear in* (event links),
 * with no curation. So it sits **beside the backlinks panel**, in the document flow, below the
 * editor — not in a drawer.
 *
 * Empty → hidden. A failed fetch → also hidden: the page itself is fine, and a red box under
 * every page because one derived panel could not load would be a bad trade (the same call
 * `BacklinksPanel` makes).
 */
import { Link } from 'react-router';
import { format } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import type { CalendarEvent } from '@/api/endpoints/calendar';
import { cn } from '@/lib/utils';
import { currentDateLocale, useTranslation } from '@/lib/i18n';
import { CategoryBadge } from '@/features/calendar/EventChip';
import { doneClass } from '@/features/calendar/eventStyle';
import { dayKeyOf, formatTime, startOfDayKey } from '@/features/calendar/datetime';
import { openEditEvent } from '@/features/calendar/eventDialogStore';
import { usePageEvents } from '@/features/calendar/useCalendar';
import { useCalendarSettings } from '@/features/calendar/useCalendarSettings';

function when(event: CalendarEvent, tz: string): string {
  const day = format(startOfDayKey(dayKeyOf(event.startsAt, tz), tz), 'EEE d MMM yyyy', {
    locale: currentDateLocale(),
  });
  return event.allDay ? day : `${day} · ${formatTime(event.startsAt, tz)}`;
}

export function EventLinksPanel({ pageId }: { pageId: string }) {
  const { t, plural } = useTranslation();
  const { timezone } = useCalendarSettings();
  const result = usePageEvents(pageId);

  const events = result.data?.events ?? [];
  if (result.isPending || result.isError || events.length === 0) return null;

  return (
    <section
      data-testid="event-links"
      aria-label={t('calendar.links.ariaLabel')}
      className="mx-auto max-w-[78ch] px-6 pb-8"
    >
      <h3 className="flex h-[var(--row-h)] items-center gap-1.5 text-xs font-medium tracking-wide text-[var(--muted-foreground)] uppercase">
        <CalendarDays className="size-[var(--icon)]" strokeWidth={1.75} />
        {plural('calendar.links.referencedBy', events.length)}
      </h3>

      <ul>
        {events.map((event) => (
          <li key={event.id}>
            {/* One click from the page to the event (`140` §M5, criterion 10). */}
            <button
              type="button"
              data-testid={`page-event-${event.id}`}
              onClick={() => {
                openEditEvent(event);
              }}
              className="flex h-[var(--row-h)] w-full items-center gap-2 rounded-sm px-1 text-left hover:bg-[var(--accent)]"
            >
              <span className={cn('min-w-0 flex-1 truncate', doneClass(event))}>{event.title}</span>
              <span className="shrink-0 text-2xs text-[var(--muted-foreground)] tabular-nums">
                {when(event, timezone)}
              </span>
              <CategoryBadge category={event.category} />
            </button>
          </li>
        ))}
      </ul>

      <p className="px-1 pt-1 text-2xs text-[var(--muted-foreground)]">
        <Link to="/calendar" className="hover:underline">
          {t('calendar.links.openAgenda')}
        </Link>
      </p>
    </section>
  );
}
